import React from 'react';
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useUser,
} from '@clerk/clerk-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'inspector' | 'lead_inspector' | 'admin';
}

/**
 * Protected Route Component
 * Ensures users are authenticated before accessing content
 * Optionally checks for specific roles
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { user } = useUser();

  return (
    <>
      <SignedIn>
        {requiredRole ? (
          <RoleGuard requiredRole={requiredRole} user={user}>
            {children}
          </RoleGuard>
        ) : (
          children
        )}
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

interface RoleGuardProps {
  requiredRole: string;
  user:
    | {
        publicMetadata?: {
          role?: string;
        };
      }
    | null
    | undefined;
  children: React.ReactNode;
}

/**
 * Role Guard Component
 * Checks if user has required role
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  requiredRole,
  user,
  children,
}) => {
  const userRole = user?.publicMetadata?.role as string | undefined;

  // Admin can access everything
  if (userRole === 'admin') {
    return <>{children}</>;
  }

  // Lead inspectors can access inspector and lead_inspector content
  if (userRole === 'lead_inspector' && requiredRole !== 'admin') {
    return <>{children}</>;
  }

  // Check if user has exact required role
  if (userRole === requiredRole) {
    return <>{children}</>;
  }

  // Access denied
  return (
    <div className="min-h-screen flex items-center justify-center bg-sand-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-stone-900 mb-4">
          Access Denied
        </h1>
        <p className="text-sand-700 mb-6">
          You don&apos;t have permission to access this page.
        </p>
        <p className="text-sm text-sand-600">
          Required role: {requiredRole}
          <br />
          Your role: {userRole || 'none'}
        </p>
      </div>
    </div>
  );
};
